import { MetadataEntry } from '../types/MetadataEntry';
import { MetadataEntryKind } from '../types/MetadataEntryKind';
import { MetadataValueKind } from '../types/MetadataValueKind';

function createMetadataId(): string {
  return crypto.randomUUID();
}

function parseScalarValue(value: unknown, key: string): MetadataEntry {
  if (typeof value === 'boolean') {
    return {
      id: createMetadataId(),
      key,
      kind: 'value',
      valueKind: 'boolean',
      scalarValue: value ? 'true' : 'false',
    };
  }

  if (typeof value === 'number') {
    return {
      id: createMetadataId(),
      key,
      kind: 'value',
      valueKind: 'number',
      scalarValue: String(value),
    };
  }

  if (typeof value === 'string') {
    return {
      id: createMetadataId(),
      key,
      kind: 'value',
      valueKind: 'string',
      scalarValue: value,
    };
  }

  return {
    id: createMetadataId(),
    key,
    kind: 'value',
    valueKind: 'string',
    scalarValue: value == null ? '' : String(value),
  };
}

function parseMetadataValue(value: unknown, key: string): MetadataEntry {
  if (Array.isArray(value)) {
    const objectItems = value.filter((item) => typeof item === 'object' && item !== null && !Array.isArray(item));

    if (objectItems.length === value.length && value.length > 0) {
      return {
        id: createMetadataId(),
        key,
        kind: 'list',
        listItemKind: 'object',
        listObjects: objectItems.map((item) =>
          Object.entries(item as Record<string, unknown>).map(([childKey, childValue]) =>
            parseMetadataValue(childValue, childKey),
          ),
        ),
      };
    }

    return {
      id: createMetadataId(),
      key,
      kind: 'list',
      listItemKind: 'text',
      listItems: value.map((item) => (item == null ? '' : String(item))),
    };
  }

  if (typeof value === 'object' && value !== null) {
    return {
      id: createMetadataId(),
      key,
      kind: 'object',
      children: Object.entries(value).map(([childKey, childValue]) =>
        parseMetadataValue(childValue, childKey),
      ),
    };
  }

  return parseScalarValue(value, key);
}

export function parseJsonMetadata(json?: string | null): MetadataEntry[] {
  if (!json?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(json) as unknown;

    if (Array.isArray(parsed)) {
      return [parseMetadataValue(parsed, 'itens')];
    }

    if (typeof parsed === 'object' && parsed !== null) {
      return Object.entries(parsed).map(([key, value]) => parseMetadataValue(value, key));
    }

    return [parseScalarValue(parsed, 'valor')];
  } catch {
    return [];
  }
}

function serializeScalar(entry: MetadataEntry): unknown {
  if (entry.valueKind === 'number') {
    const parsed = Number(entry.scalarValue);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (entry.valueKind === 'boolean') {
    return entry.scalarValue === 'true';
  }

  return entry.scalarValue ?? '';
}

function serializeMetadataEntry(entry: MetadataEntry): unknown {
  if (entry.kind === 'value') {
    return serializeScalar(entry);
  }

  if (entry.kind === 'list') {
    if (entry.listItemKind === 'object') {
      return (entry.listObjects ?? [])
        .map((objectFields) => {
          const objectValue: Record<string, unknown> = {};
          for (const field of objectFields) {
            if (!field.key.trim()) {
              continue;
            }
            objectValue[field.key.trim()] = serializeMetadataEntry(field);
          }
          return objectValue;
        })
        .filter((item) => Object.keys(item).length > 0);
    }

    return (entry.listItems ?? []).map((item) => item.trim()).filter(Boolean);
  }

  const objectValue: Record<string, unknown> = {};
  for (const child of entry.children ?? []) {
    if (!child.key.trim()) {
      continue;
    }
    objectValue[child.key.trim()] = serializeMetadataEntry(child);
  }
  return objectValue;
}

export function serializeJsonMetadata(entries: MetadataEntry[]): string {
  const meaningfulEntries = entries.filter((entry) => entry.key.trim() || entry.kind !== 'value');

  if (meaningfulEntries.length === 0) {
    return '';
  }

  if (meaningfulEntries.length === 1 && meaningfulEntries[0]?.key === 'itens') {
    return JSON.stringify(serializeMetadataEntry(meaningfulEntries[0]));
  }

  const objectValue: Record<string, unknown> = {};
  for (const entry of meaningfulEntries) {
    if (!entry.key.trim()) {
      continue;
    }
    objectValue[entry.key.trim()] = serializeMetadataEntry(entry);
  }

  return JSON.stringify(objectValue);
}

export function createEmptyMetadataEntry(kind: MetadataEntryKind = 'value'): MetadataEntry {
  if (kind === 'object') {
    return { id: createMetadataId(), key: '', kind: 'object', children: [] };
  }

  if (kind === 'list') {
    return {
      id: createMetadataId(),
      key: '',
      kind: 'list',
      listItemKind: 'text',
      listItems: [''],
    };
  }

  return {
    id: createMetadataId(),
    key: '',
    kind: 'value',
    valueKind: 'string',
    scalarValue: '',
  };
}

export function metadataEntryKindLabel(kind: MetadataEntryKind): string {
  switch (kind) {
    case 'value':
      return 'Valor';
    case 'object':
      return 'Grupo';
    case 'list':
      return 'Lista';
    default:
      return kind;
  }
}

export function metadataValueKindLabel(kind: MetadataValueKind): string {
  switch (kind) {
    case 'string':
      return 'Texto';
    case 'number':
      return 'Número';
    case 'boolean':
      return 'Sim/Não';
    default:
      return kind;
  }
}
